import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { createStore, entries, setMany } from 'idb-keyval';
/**
 * Per-account IndexedDB isolation for the multi-account web feature.
 *
 * Each account's persisted data (React Query cache, Eppo flags) lives in a physically
 * separate IndexedDB database, named `{base}-{userId}`. With no active account the base
 * name is returned unchanged, so single-account behavior is identical to today.
 */
export function accountScopedDbName(baseDbName, accountId = getActiveAccountId()) {
    if (!accountId) {
        return baseDbName;
    }
    return `${baseDbName}-${accountId}`;
}
/**
 * Returns an account-scoped idb-keyval store, or `undefined` when no account is active so
 * callers can fall back to idb-keyval's default store (today's behavior). `storeName`
 * defaults to idb-keyval's own default object-store name, so the only change versus the
 * default store is the account-scoped database name.
 */
export function getAccountScopedStore(baseDbName, accountId = getActiveAccountId(), storeName = 'keyval') {
    if (!accountId) {
        return undefined;
    }
    return createStore(`${baseDbName}-${accountId}`, storeName);
}
/**
 * Seeds an account store from the pre-multiaccount default store by copying every entry
 * not already present in `target`. Copy, not move: the original default-store data is left
 * intact so disabling multi-account falls back cleanly.
 */
export async function migrateDefaultStoreToAccount(target) {
    const sourceEntries = await entries();
    if (sourceEntries.length === 0) {
        return;
    }
    const existingKeys = new Set((await entries(target)).map(([key]) => String(key)));
    const toCopy = sourceEntries.filter(([key]) => !existingKeys.has(String(key)));
    if (toCopy.length > 0) {
        await setMany(toCopy, target);
    }
}
/**
 * Deletes an account's IndexedDB database (`{baseDbName}-{accountId}`) — used when signing out a
 * single account so its persisted data is physically removed.
 *
 * Never rejects, so a held connection can't hang or break sign-out (best-effort, like the other
 * clears). Resolves `true` when the database was deleted or the delete was queued behind another
 * open connection (`blocked` — it completes once that connection closes, so it isn't a failure),
 * and `false` only on a genuine delete error. The orphaned data is inert without the session
 * either way; the boolean lets the caller — which has the logger; `common` can't depend on it —
 * track a recurring failure.
 */
export function deleteAccountStore(baseDbName, accountId) {
    return new Promise((resolve) => {
        const request = indexedDB.deleteDatabase(accountScopedDbName(baseDbName, accountId));
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
        request.onblocked = () => resolve(true);
    });
}
//# sourceMappingURL=accountScopedIDB.js.map